import { keepPreviousData, useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { BatteryCharging, ChevronRight, Clock, Crosshair, Drone, Fuel, MapPin, Mountain, Route, Swords, Users, Wrench } from "lucide-react";
import type { ReactNode } from "react";

import { api } from "../../api/client";
import type { DeployedUnit, Logistics, LogisticsAssumptions, LogisticsDrones, LogisticsLine, Phase, SmallDronesCause } from "../../api/types";
import { Callout } from "../../components/Callout";
import { Collapsible } from "../../components/Collapsible";
import { EmptyState, Spinner } from "../../components/EmptyState";
import { PanelGroup, PanelRow, StatTile } from "../../components/panel";
import { Motion } from "../../components/presence";
import { Badge, FloatingPanel } from "../../components/ui";
import { VIEW_META } from "../../components/viewMeta";
import { HelpButton } from "../../help/HelpButton";
import { unitDesignation } from "../../i18n/designation";
import { formatNumber, k, locale, t, tServer } from "../../i18n/i18n";
import { missionName } from "../../i18n/vocabulary";
import { symbolSvg } from "../../map/units/symbols";
import { effectiveOpo, useDeployment } from "../../map/units/useDeployment";
import { plural } from "../../plural";
import { useUi } from "../../state/ui";
import { appliedWeatherLabel } from "../../weather";
import { OpenMissionDataButton } from "../openMissionData";
import { zoneRefusalOf } from "../../map/zones/zoneRefusal";
import { ZoneWaitCallout } from "../../map/zones/ZoneWaitCallout";
import { Waiting } from "../../components/Waiting";
import { AssumptionsBadge, customOf, ResetAssumptions, Slider, type SliderSpec, useSaveAssumptions } from "./assumptions";

/** Logistique S4 (vue « Logistique », registre D16) : l'« Estimation logistique » de l'iPad, en UE et UF, en
 *  direct depuis la carte — unités du temps affiché dans la barre du bas, déplacements et potentiels compris,
 *  coefficient de mobilité de l'exercice, posture de la mission ; hypothèses réglables, propres au dossier. */

const num = (value: number, digits = 1) => formatNumber(value, { minimumFractionDigits: digits, maximumFractionDigits: digits });
const ue = (value: number) => num(value, value < 1 ? 2 : 1);
const liters = (value: number) => formatNumber(Math.round(value));

const SLIDERS: SliderSpec[] = [
  { key: "liters_tracked", label: k("Chenillé"), digits: 0, unit: k("L/100 km") },
  { key: "liters_wheeled", label: k("À roues"), digits: 0, unit: k("L/100 km") },
  { key: "vehicles_per_unit", label: k("Véhicules par unité (repli)"), digits: 0 },
  { key: "base_speed_kmh", label: k("Vitesse"), digits: 0, unit: k("km/h") },
  { key: "uf_per_combat_day", label: k("Intensité"), digits: 2, unit: k("UF/jour") },
  { key: "combat_days", label: k("Jours de combat"), digits: 1 },
  { key: "contact_range_km", label: k("Portée de contact"), digits: 1, unit: k("km") },
];

/** Hypothèses drones (chantier « Drones ») : montrées seulement quand le dossier a des moyens drones. */
const DRONE_SLIDERS: SliderSpec[] = [
  { key: "drone_flight_hours", label: k("Heures de vol par drone et par jour"), digits: 0, unit: k("h") },
  { key: "drone_battery_minutes", label: k("Autonomie d'une batterie"), digits: 0, unit: k("min") },
  { key: "mto_strikes_per_day", label: k("Frappes par patrouille et par jour"), digits: 0 },
];

/** Hypothèses de la vue (ravitaillement et drones) : celles de la maintenance se règlent dans la vue Maintenance. */
const LOGISTICS_KEYS: readonly (keyof LogisticsAssumptions)[] = [...SLIDERS, ...DRONE_SLIDERS].map((s) => s.key);

/** Causes d'une autonomie réduite (état des petits drones de la météo appliquée). */
const AUTONOMY_CAUSES: Partial<Record<SmallDronesCause, string>> = {
  rain: k("pluie"),
  wind: k("vent"),
  cold: k("froid"),
  heat: k("chaleur"),
};

/** « 12 km », « 800 m » : distance d'un plot à l'ennemi le plus proche. */
const distance = (meters: number) => (meters >= 1000 ? `${num(meters / 1000, meters < 10_000 ? 1 : 0)} km` : `${Math.round(meters)} m`);

/** Groupe « Drones » de la logistique (chantier « Drones ») : batteries et munitions téléopérées par jour, autonomie
 *  réduite par la météo, plots logistiques au temps affiché exposés aux drones ennemis et leur protection. */
export function DronesLogistics({ drones, days }: { drones: LogisticsDrones; days: number }) {
  const causes = drones.causes.flatMap((c) => (AUTONOMY_CAUSES[c] ? [t(AUTONOMY_CAUSES[c])] : []));
  const exposed = drones.plots.filter((p) => p.exposed);
  return (
    <PanelGroup title={t("Drones")} leading={<span className="flex shrink-0 text-ink-muted"><Drone size={14} /></span>}>
      <div className="space-y-2 px-4 pb-1">
        <div className="grid grid-cols-2 gap-2">
          <StatTile
            icon={BatteryCharging}
            value={formatNumber(drones.batteries_per_day)}
            label={t("Batteries par jour")}
            hint={t("{vectors} drones · {minutes} min par batterie", { vectors: drones.vectors, minutes: drones.autonomy_min })}
          />
          {drones.mto_per_day !== null && (
            <StatTile
              icon={Crosshair}
              value={formatNumber(drones.mto_per_day)}
              label={t("Munitions téléopérées par jour")}
              hint={days !== 1 ? t("{total} sur {days} jours de combat", { total: formatNumber(Math.round(drones.mto_per_day * days)), days: num(days, days % 1 ? 1 : 0) }) : undefined}
            />
          )}
        </div>
        {drones.autonomy_factor < 1 && causes.length > 0 && (
          <p className="text-meta leading-snug text-warn">
            {t("Autonomie réduite à {minutes} min par la météo : {causes}.", { minutes: drones.autonomy_min, causes: causes.join(", ") })}
          </p>
        )}
      </div>
      {drones.plots.length > 0 && (
        <>
          <ul className="space-y-px">
            {drones.plots.map((plot) => (
              <li key={plot.key}>
                <PanelRow
                  icon={<MapPin size={12} className="text-ink-muted" />}
                  title={<span className="font-semibold">{unitDesignation(plot.name)}</span>}
                  detail={
                    plot.distance_eni_m === null
                      ? t("Aucun ennemi placé")
                      : plot.exposed
                        ? undefined
                        : t("À {distance} de l'ennemi", { distance: distance(plot.distance_eni_m) })
                  }
                  state={
                    plot.exposed && plot.distance_eni_m !== null ? (
                      <Badge dot tone="warn">
                        {t("exposé à {km} km", { km: num(plot.distance_eni_m / 1000, plot.distance_eni_m < 10_000 ? 1 : 0) })}
                      </Badge>
                    ) : undefined
                  }
                />
              </li>
            ))}
          </ul>
          <p className={clsx("px-4 pt-1.5 text-meta leading-snug", exposed.length ? "text-ink-soft" : "text-ink-muted")}>
            {exposed.length
              ? t("Plots à portée des drones ENI : sous couvert, dispersés, guet aérien permanent, camouflage 3D.")
              : t("Plots hors de portée des drones ENI (15 km).")}
          </p>
        </>
      )}
    </PanelGroup>
  );
}

/** Situation de l'estimation, avec la météo que les ordres appliquent et ses effets sur le mouvement (serveur
 *  `logistics.payload`) : identifiant appliqué (« auto » : météo de saison), vent régional de la carte, majoration
 *  des temps de mouvement de la ligne « Délais » de l'ordre. */
export type LogisticsSituation = Logistics["situation"] & {
  weather_key?: string;
  regional_wind?: string | null;
  delay_pct?: number;
};

/** « Coefficient de mobilité 0,65 (Mailly-le-Camp, novembre, météo Brouillard, vent faible, 2 à 8 °C) : temps de
 *  mouvement majorés de 55 % (météo) ». Mois et météo nommés dans la langue de l'interface : la météo APPLIQUÉE par
 *  les ordres (une météo choisie avec les mots du réglage, un scénario importé avec le libellé du serveur), rien pour
 *  la météo de saison ; puis les effets sur le mouvement : détours du terrain seul, majoration de la ligne « Délais »
 *  de l'ordre (aucune par temps qui n'est pas défavorable au mouvement). */
export function mobilityLabel(s: LogisticsSituation): string {
  const month = new Date(2000, s.month - 1, 1).toLocaleDateString(locale(), { month: "long" });
  const applied = appliedWeatherLabel(s.weather_key, s.weather, s.regional_wind);
  const weather = applied ? [t("météo {weather}", { weather: applied })] : [];
  const text = t("Coefficient de mobilité {value} ({context})", {
    value: num(s.mobility, 2),
    context: [s.map ?? t("camp inconnu"), month, ...weather].join(", "),
  });
  const effects = [
    ...(s.detour && s.detour > 1 ? [t("détours du terrain × {detour}", { detour: num(s.detour, 2) })] : []),
    ...(s.delay_pct ? [t("temps de mouvement majorés de {pct} % (météo)", { pct: s.delay_pct })] : []),
  ];
  return effects.length ? t("{mobility} : {effects}", { mobility: text, effects: effects.join(t(" ; ")) }) : text;
}

/** Élément de la situation (mission, mobilité, forces) : icône grise de 12 px et texte. */
export function SituationLine({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <p className="flex items-start gap-2 text-meta leading-snug text-ink-soft">
      <span className="mt-px shrink-0 text-ink-muted">{icon}</span>
      <span>{children}</span>
    </p>
  );
}

/** D'où vient la distance planifiée d'une unité (déplacements de la carte par la route ou à vol d'oiseau, ou
 *  estimation selon la posture pour une unité qui ne bouge pas). */
const ROUTE_LABEL: Record<LogisticsLine["route"], string> = {
  road: k("trajet par la route"),
  map: k("trajet de la carte, détours compris"),
  estimate: k("estimation selon la posture"),
};

/** Besoin d'une unité : symbole APP-6 de l'unité (retrouvé dans le dispositif ; repli : anneau carré si chenillé,
 *  rond si à roues), désignation, contact, matériel et parc, origine du trajet, puis UE, distance, litres et UF à
 *  droite. */
function UnitLine({ line, unit }: { line: LogisticsLine; unit: DeployedUnit | undefined }) {
  const details = {
    materiel: line.materiel ?? t("matériel inconnu"),
    vehicles: num(line.vehicles, 0),
    potential: Math.round(line.potential * 100),
  };
  const fleet = line.vehicles_known
    ? t("{materiel} · {vehicles} véh. · potentiel {potential} %", details)
    : t("{materiel} · {vehicles} véh. (hypothèse) · potentiel {potential} %", details);
  const mobility = line.tracked ? t("Chenillé") : t("À roues");
  return (
    <PanelRow
      icon={
        unit ? (
          <span
            title={mobility}
            className="flex h-4 w-6 items-center justify-center [&>svg]:max-h-4 [&>svg]:max-w-6"
            dangerouslySetInnerHTML={{ __html: symbolSvg(unit.sidc, 13) }}
          />
        ) : (
          <span
            className="size-2.5 shrink-0 rounded-full border-2 border-friend"
            style={{ borderRadius: line.tracked ? 3 : 999 }}
            title={mobility}
          />
        )
      }
      title={
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span className="font-semibold">{unitDesignation(line.name)}</span>
          {line.contact && (
            <Badge dot tone="hostile">
              {t("Contact")}
            </Badge>
          )}
        </span>
      }
      detail={
        <>
          <span className="block">{fleet}</span>
          <span className="block">{t(ROUTE_LABEL[line.route])}</span>
        </>
      }
      state={
        <span className="shrink-0 text-right text-meta leading-snug tabular-nums">
          <span className="block font-semibold text-ink">
            {ue(line.ue)} {t("UE")}
          </span>
          <span className="block text-ink-muted">
            {num(line.planned_km)} km · {liters(line.liters)} L
          </span>
          {line.contact && (
            <span className="block text-ink-muted">
              {num(line.uf)} {t("UF")}
            </span>
          )}
        </span>
      }
    />
  );
}

/** Estimation logistique du dossier au temps `phase` (serveur `logistics.payload`), partagée par la vue Logistique et
 *  la fiche d'unité (CAPOPS, chantier « DTO / CAPOPS ») : même clé, donc même cache et même invalidation
 *  (déplacement, potentiel, hypothèses) — aucune requête de plus quand la vue est ouverte. `enabled` : la fiche ne la
 *  demande que pour une unité amie. */
export function useLogistics(exerciseId: string | null, phase: Phase, enabled = true) {
  const params = useUi((s) => s.deploymentParams);
  const opo = effectiveOpo(params, useUi((s) => s.opo));
  const { data: exercises } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises?.find((e) => e.id === exerciseId);
  const complete = Boolean(exercise?.map_slug && exercise.parameters.level && exercise.parameters.odb);
  const centac = exercise?.parameters.level === "CENTAC";
  // Le dossier change (déplacement, potentiel, Données initiales) : l'estimation suit la carte.
  const { data: deployment } = useDeployment();
  const version = deployment?.exercise_version ?? exercise?.version;
  const key = ["logistics", exerciseId, phase, opo, version] as const;
  const query = useQuery({
    queryKey: key,
    queryFn: () => api.logistics(exerciseId as string, phase, opo),
    enabled: Boolean(exerciseId && complete && enabled),
    placeholderData: keepPreviousData,
    retry: false,
  });
  return { query, key, opo, complete, centac, deployment };
}

/** Renvoi vers la vue Maintenance (DTO, CAPOPS), de nature différente du ravitaillement : une ligne à part, sous le
 *  détail des besoins. */
export function MaintenanceLink() {
  const setView = useUi((s) => s.setView);
  return (
    <PanelGroup flush>
      <PanelRow
        icon={<Wrench size={14} />}
        title={t("Maintenance")}
        detail={t("DTO par famille de matériel et CAPOPS des unités, dans leur vue.")}
        state={<ChevronRight size={14} className="text-ink-muted" aria-hidden />}
        onClick={() => setView("maintenance")}
        clickTitle={t("Ouvrir la maintenance")}
      />
    </PanelGroup>
  );
}

export function LogisticsPanel() {
  const exerciseId = useUi((s) => s.exerciseId);
  const phase = useUi((s) => s.phase);
  const { query, key, opo, complete, centac, deployment } = useLogistics(exerciseId, phase);
  const { save, reverts } = useSaveAssumptions(exerciseId, phase, opo, query, key);
  const data = query.data;
  const s: LogisticsSituation | undefined = data?.situation;
  const totals = data?.totals;

  const unitsByKey = new Map((deployment?.units ?? []).map((u) => [u.key, u]));

  return (
    <FloatingPanel
      enter="left"
      width="view"
      icon={VIEW_META.logistics.icon}
      title={t("Logistique")}
      actions={<HelpButton screen="logistique" buttonSize="sm" size={16} />}
    >
      {!exerciseId || !complete ? (
        <Motion origin="fade">
          <EmptyState variant="inline" action={<OpenMissionDataButton />}>
            <p className="text-ink-soft">
              {t(
                "L'estimation logistique mesure, en unités d'essence (UE) et en unités de feu (UF), ce que demande la manœuvre du dispositif affiché sur la carte.",
              )}
            </p>
            <p className="mt-2">
              {!exerciseId ? t("Pour estimer la logistique, créez d'abord le dossier.") : t("Complétez les Données initiales.")}
            </p>
          </EmptyState>
        </Motion>
      ) : query.isError ? (
        <div className="px-4 py-4">
          {zoneRefusalOf(query.error) ? (
            <ZoneWaitCallout message={query.error.message} error={query.error} />
          ) : (
            <Callout tone="danger">{query.error.message}</Callout>
          )}
        </div>
      ) : !data || !s ? (
        <div className="flex items-center gap-2 px-4 py-6 text-ui text-ink-muted">
          <Spinner decorative /> {<Waiting>{t("Calcul de l'estimation…")}</Waiting>}
        </div>
      ) : (
        <Motion origin="fade" className={clsx("transition-opacity", query.isFetching && "opacity-80")}>
          <PanelGroup title={t("En direct depuis la carte · temps {phase}", { phase: data.phase })} flush>
            <div className="space-y-1.5 px-4 pb-1">
              <SituationLine icon={<Route size={12} />}>
                {s.mission == null ? t("Mission non choisie") : centac ? tServer(s.mission) : missionName(s.mission)} —{" "}
                {tServer(s.posture)}
              </SituationLine>
              <SituationLine icon={<Mountain size={12} />}>{mobilityLabel(s)}</SituationLine>
              <SituationLine icon={<Users size={12} />}>{t("{ami} AMI · {eni} ENI", { ami: s.ami, eni: s.eni })}</SituationLine>
            </div>
          </PanelGroup>

          {totals ? (
            <>
              <PanelGroup flush>
                <div className="grid grid-cols-2 gap-2 px-4 py-1">
                  <StatTile icon={Route} value={num(totals.max_km)} unit="km" label={t("Mouvement maximal planifié")} />
                  {/* Chaque tuile a un nom ; la conversion en litres et le nombre d'unités au contact en précisent la valeur. */}
                  <StatTile
                    icon={Fuel}
                    value={ue(totals.ue)}
                    unit={t("UE")}
                    label={t("Carburant")}
                    hint={`≈ ${liters(totals.liters)} L`}
                  />
                  {/* Durée majorée comme les temps de mouvement de l'ordre par mauvais temps : la majoration en précise
                      la valeur. */}
                  <StatTile
                    icon={Clock}
                    value={num(totals.hours)}
                    unit="h"
                    label={t("Mouvement")}
                    hint={s.delay_pct ? t("majoré de {pct} % (météo)", { pct: s.delay_pct }) : undefined}
                  />
                  {/* UF par unité au contact : les UF d'armes différentes ne s'additionnent pas (détail par nature
                      de matériel plus bas). */}
                  <StatTile
                    icon={Crosshair}
                    value={num(totals.uf_each)}
                    unit={t("UF")}
                    label={t("Munitions par unité au contact")}
                    hint={plural(totals.contacts, t("unité au contact"), t("unités au contact"))}
                  />
                </div>
              </PanelGroup>
              {data.uf_by_nature.length > 0 && (
                <PanelGroup title={t("Munitions par nature de matériel")} count={data.uf_by_nature.length}>
                  <ul className="space-y-px">
                    {data.uf_by_nature.map((n) => (
                      <li key={n.materiel ?? ""}>
                        <PanelRow
                          icon={<Crosshair size={12} className="text-ink-muted" />}
                          title={<span className="font-semibold">{n.materiel ?? t("matériel inconnu")}</span>}
                          detail={plural(n.units, t("unité au contact"), t("unités au contact"))}
                          state={
                            <span className="shrink-0 text-right text-meta font-semibold text-ink tabular-nums">
                              {num(n.uf)} {t("UF")}
                            </span>
                          }
                        />
                      </li>
                    ))}
                  </ul>
                </PanelGroup>
              )}
              <PanelGroup title={t("Par unité")} count={data.lines.length}>
                <ul className="space-y-px">
                  {data.lines.map((line) => (
                    <li key={line.key}>
                      <UnitLine line={line} unit={unitsByKey.get(line.key)} />
                    </li>
                  ))}
                </ul>
              </PanelGroup>
            </>
          ) : (
            <p className="flex items-start gap-2 border-b border-line px-4 py-3.5 text-ui leading-relaxed text-ink-soft">
              <Swords size={14} className="mt-0.5 shrink-0 text-ink-muted" />
              {data.empty === "no_ami"
                ? t("Aucune unité amie n'est placée au temps {phase}.", { phase: data.phase })
                : t("Aucun ennemi n'est placé au temps {phase} : la profondeur de la manœuvre se mesure entre les deux dispositifs.", {
                    phase: data.phase,
                  })}
            </p>
          )}

          {data.drones && <DronesLogistics drones={data.drones} days={data.assumptions.combat_days} />}

          {data.maintenance && <MaintenanceLink />}

          <div>
            <Collapsible title={t("Définitions UE et UF")}>
              <div className="space-y-2 text-justify text-meta leading-relaxed text-ink-soft hyphens-auto">
                <p>
                  <strong className="font-semibold text-ink">{t("UE (unité d'essence)")}</strong>
                  {t(
                    " : quantité de carburant nécessaire pour faire mouvement sur 100 km. Besoin = distance planifiée / 100 ; la distance planifiée suit les déplacements de l'unité sur la carte jusqu'à T3, par la route quand le terrain a un réseau routier (sinon à vol d'oiseau, détours compris) ; une unité qui ne bouge pas garde l'estimation selon la posture. Les litres convertissent le besoin par le parc de l'unité (ordre de grandeur de son échelon : GTIA 300 à 350 véhicules, brigade 2 000, division 6 000 à 8 000) et les taux L/100 km des hypothèses, engins chenillés et à roues séparés.",
                  )}
                </p>
                <p>
                  <strong className="font-semibold text-ink">{t("UF (unité de feu)")}</strong>
                  {t(
                    " : consommation moyenne journalière en munitions d'une arme en combat de moyenne intensité, unité de référence des calculs de dotation. Besoin de chaque unité au contact = intensité (UF par jour de combat) × jours de combat planifiés. Une UF est propre à chaque arme : les besoins se lisent unité par unité et ne s'additionnent qu'entre matériels de même nature.",
                  )}
                </p>
              </div>
            </Collapsible>
            <Collapsible
              title={t("Hypothèses (réglables)")}
              trailing={<AssumptionsBadge data={data} custom={customOf(data, LOGISTICS_KEYS)} />}
            >
              <div className="space-y-3">
                {[...SLIDERS, ...(data.drones ? DRONE_SLIDERS : [])].map((spec) => (
                  <Slider
                    key={`${spec.key}-${reverts}`}
                    spec={spec}
                    value={data.assumptions[spec.key]}
                    bounds={data.bounds[spec.key]}
                    disabled={save.isPending}
                    onCommit={(value) => save.mutate({ [spec.key]: value })}
                  />
                ))}
                <ResetAssumptions
                  data={data}
                  keys={LOGISTICS_KEYS}
                  custom={customOf(data, LOGISTICS_KEYS)}
                  pending={save.isPending}
                  onReset={(values) => save.mutate(values)}
                />
              </div>
            </Collapsible>
          </div>

          <p className="border-t border-line px-4 py-3 text-justify text-meta leading-relaxed text-ink-muted hyphens-auto">
            {t(
              "Carburant de mouvement uniquement (stationnaire de combat et groupes électrogènes exclus). Le besoin en UE est dimensionné par l'élément le plus éloigné ; le besoin en UF suppose la moyenne intensité à × 1. Ordres de grandeur de planification : parcs doctrinaux et taux réglables, pas des valeurs réglementaires.",
            )}
          </p>
        </Motion>
      )}
    </FloatingPanel>
  );
}
